
import React from 'react';
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, BarChart, Bot, Share, Database, Clock, Code } from 'lucide-react';

const ServicesSection: React.FC = () => {
  return (
    <section className="py-16 md:py-24">
      <div className="container max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
              Our Services
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Technical & Consulting Services
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Our team is continuously refining each workstream with the goal of providing maximum possible value in terms of data utilization. We offer two main types of services:
            </p>
            
            <Tabs defaultValue="consulting" className="mb-8">
              <TabsList className="w-full grid grid-cols-2">
                <TabsTrigger value="consulting">Consulting Services</TabsTrigger>
                <TabsTrigger value="technical">Technical Solutions</TabsTrigger>
              </TabsList>
              <TabsContent value="consulting" className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <p className="text-muted-foreground">Setting of use cases and specifications for tailored solutions</p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <p className="text-muted-foreground">Support with adoption using rules and principles based on our experience</p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <p className="text-muted-foreground">Training programs to maximize value from reporting solutions</p>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="technical" className="pt-6">
                <div className="space-y-6">
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <div className="flex items-center mb-2">
                      <BarChart className="h-5 w-5 text-primary mr-2" />
                      <h4 className="font-medium">Reporting Solutions</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">Provide data visibility and enable data-driven decision making across your organization</p>
                  </div>
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <div className="flex items-center mb-2">
                      <Bot className="h-5 w-5 text-primary mr-2" />
                      <h4 className="font-medium">Automation Solutions</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">Based on the same or similar data as reporting solutions, these put your data to work for you</p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            
            <Alert className="bg-secondary/50 border-primary/20">
              <Share className="h-4 w-4 text-primary" />
              <AlertTitle className="text-base">Platform Agnostic</AlertTitle>
              <AlertDescription className="text-sm text-muted-foreground">
                We build solutions on prevalent cloud platforms (Azure, AWS, GCP) but can accommodate custom requests. We don't ask you to bend towards our tech—we adapt to you.
              </AlertDescription>
            </Alert>
          </div>
          
          <div className="reveal">
            <div className="bg-white rounded-2xl shadow-lg border border-border overflow-hidden">
              <div className="p-8">
                <h3 className="text-xl font-semibold mb-6">Technical Implementation Benefits</h3>
                
                <div className="space-y-6 mb-6">
                  <div className="flex">
                    <div className="mr-4 p-2 bg-primary/10 rounded-lg h-min">
                      <Database className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">Shared Data Backend</h4>
                      <p className="text-sm text-muted-foreground">All workstreams share the same backend data warehouse, making delivery of additional workstreams faster</p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="mr-4 p-2 bg-primary/10 rounded-lg h-min">
                      <Clock className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">Faster Implementation Time</h4>
                      <p className="text-sm text-muted-foreground">After data for one workstream is collected, it can be used for others, reducing delivery time</p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="mr-4 p-2 bg-primary/10 rounded-lg h-min">
                      <Code className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">Flexible Solutions</h4>
                      <p className="text-sm text-muted-foreground">Pre-made solutions that are flexible and ready to be customized to fit your specific needs</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-6 border-t border-border">
                  <p className="text-muted-foreground text-sm italic">
                    "You can view each workstream as a sum of our knowledge to date. We are more than happy to learn more on the new solutions needed for your specific business case."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
